/* eslint-disable max-lines */
import { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TagsInput,
  TagsInputInput,
  TagsInputItem,
  TagsInputList,
  Button,
  LoadingButton,
  Spinner,
  Stack,
  Typography,
  toast,
} from "@altimateai/lego";
import {
  useDatabricksServicePrincipalConfig,
  useUpsertDatabricksServicePrincipalConfig,
} from "../hooks";
import { ServicePrincipalConfig, ServicePrincipalConfigSchema } from "../types";
import { isValidRegex, parseSaveError, sameRuleSet } from "./servicePrincipalUtils";
import ServicePrincipalTestResult from "./ServicePrincipalTestResult";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const dedupe = (values: string[]) => Array.from(new Set(values));

const EMPTY_FORM: ServicePrincipalConfig = { prefixes: [], regexes: [] };

export const ServicePrincipalConfigModal = ({ open, onOpenChange }: Props) => {
  const { data, isLoading, isError, refetch } = useDatabricksServicePrincipalConfig(open);
  const upsertMutation = useUpsertDatabricksServicePrincipalConfig();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ServicePrincipalConfig>({
    resolver: zodResolver(ServicePrincipalConfigSchema),
    defaultValues: EMPTY_FORM,
  });

  const [serverRegexErrors, setServerRegexErrors] = useState<Set<string>>(new Set());
  const [testIdentifier, setTestIdentifier] = useState("");
  const [tab, setTab] = useState<"prefixes" | "regexes">("prefixes");
  // Seed once per modal-open cycle so a refetch cannot clobber unsaved edits.
  const seededRef = useRef<ServicePrincipalConfig | null>(null);

  useEffect(() => {
    if (!open) {
      seededRef.current = null;
      return;
    }
    if (seededRef.current || !data) return;
    reset(data);
    setServerRegexErrors(new Set());
    setTestIdentifier("");
    setTab("prefixes");
    clearErrors();
    seededRef.current = data;
  }, [open, data, reset, clearErrors]);

  const prefixes = watch("prefixes");
  const regexes = watch("regexes");
  const baseline = seededRef.current ?? EMPTY_FORM;
  const isDirty =
    !sameRuleSet(prefixes, baseline.prefixes) || !sameRuleSet(regexes, baseline.regexes);

  const onSubmit = handleSubmit(async values => {
    try {
      await upsertMutation.mutateAsync(values);
      toast({ title: "Classification rules saved" });
      onOpenChange(false);
    } catch (err: unknown) {
      const { message, offendingRegexes } = parseSaveError(err, values.regexes);
      if (offendingRegexes.size > 0) {
        setServerRegexErrors(offendingRegexes);
        setTab("regexes");
      }
      toast({ variant: "destructive", title: "Failed to save", description: message });
    }
  });

  const handleRegexValidate = (value: string): boolean => {
    if (!isValidRegex(value)) {
      setError("regexes", { type: "manual", message: `Invalid regex: ${value}` });
      return false;
    }
    clearErrors("regexes");
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="al-max-w-xl">
        <DialogHeader>
          <DialogTitle>Service Principal Classification</DialogTitle>
          <DialogDescription>
            Identifiers from query history are classified as Service Principal when any rule
            matches.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <Stack align="center" justify="center" className="al-py-8">
            <Spinner />
          </Stack>
        ) : isError ? (
          <Stack align="center" justify="center" direction="column" gap="sm" className="al-py-8">
            <Typography className="al-text-destructive">
              Failed to load classification rules.
            </Typography>
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </Stack>
        ) : (
          <Stack direction="column" gap="md">
            <Tabs value={tab} onValueChange={v => setTab(v as "prefixes" | "regexes")}>
              <TabsList>
                <TabsTrigger value="prefixes">Prefixes ({prefixes.length})</TabsTrigger>
                <TabsTrigger value="regexes">Regex patterns ({regexes.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="prefixes">
                <Stack direction="column" gap="sm" className="al-pt-2">
                  <Typography size="sm" className="al-text-muted-foreground">
                    Matched at the start of the identifier. Literal, case-sensitive.
                  </Typography>
                  <Controller
                    control={control}
                    name="prefixes"
                    render={({ field }) => (
                      <TagsInput
                        value={field.value}
                        onValueChange={next => field.onChange(dedupe(next))}
                        addOnPaste
                        addOnTab
                        blurBehavior="add"
                        delimiter=","
                        className="al-w-full"
                      >
                        <TagsInputList>
                          {field.value.map(p => (
                            <TagsInputItem key={p} value={p}>
                              {p}
                            </TagsInputItem>
                          ))}
                          <TagsInputInput
                            placeholder="Add prefix and press Enter (e.g. svc)"
                            aria-label="Add prefix"
                          />
                        </TagsInputList>
                      </TagsInput>
                    )}
                  />
                </Stack>
              </TabsContent>

              <TabsContent value="regexes">
                <Stack direction="column" gap="sm" className="al-pt-2">
                  <Typography size="sm" className="al-text-muted-foreground">
                    JavaScript regex syntax. Anchor with <code>^…$</code> for exact match.
                  </Typography>
                  <Controller
                    control={control}
                    name="regexes"
                    render={({ field }) => (
                      <TagsInput
                        value={field.value}
                        onValueChange={next => {
                          const unique = dedupe(next);
                          field.onChange(unique);
                          clearErrors("regexes");
                          if (serverRegexErrors.size > 0) {
                            setServerRegexErrors(
                              new Set([...serverRegexErrors].filter(v => unique.includes(v)))
                            );
                          }
                        }}
                        onValidate={handleRegexValidate}
                        addOnPaste
                        addOnTab
                        blurBehavior="add"
                        delimiter=","
                        className="al-w-full"
                      >
                        <TagsInputList>
                          {field.value.map(r => (
                            <TagsInputItem
                              key={r}
                              value={r}
                              className={
                                serverRegexErrors.has(r)
                                  ? "al-bg-red-100 al-text-red-800 al-border-red-200"
                                  : undefined
                              }
                            >
                              {r}
                            </TagsInputItem>
                          ))}
                          <TagsInputInput
                            placeholder="Add regex and press Enter (e.g. ^app_.*@corp\.com$)"
                            aria-label="Add regex"
                          />
                        </TagsInputList>
                      </TagsInput>
                    )}
                  />
                  {errors.regexes?.message && (
                    <Typography size="xs" className="al-text-destructive">
                      {errors.regexes.message}
                    </Typography>
                  )}
                  {serverRegexErrors.size > 0 && (
                    <Typography role="alert" size="xs" className="al-text-destructive">
                      {`${serverRegexErrors.size} regex pattern${serverRegexErrors.size === 1 ? "" : "s"} rejected by the server — see highlighted ${serverRegexErrors.size === 1 ? "chip" : "chips"}.`}
                    </Typography>
                  )}
                </Stack>
              </TabsContent>
            </Tabs>

            <ServicePrincipalTestResult
              value={testIdentifier}
              onChange={setTestIdentifier}
              prefixes={prefixes}
              regexes={regexes}
            />
          </Stack>
        )}

        <DialogFooter>
          <Stack direction="row" align="center" justify="between" className="al-w-full al-gap-2">
            <Typography size="xs" className="al-text-muted-foreground">
              {`${prefixes.length} prefix${prefixes.length === 1 ? "" : "es"} · ${regexes.length} regex${regexes.length === 1 ? "" : "es"} will be saved`}
            </Typography>
            <Stack direction="row" gap="sm">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <LoadingButton
                loading={upsertMutation.isPending}
                disabled={!isDirty || !data || upsertMutation.isPending}
                onClick={onSubmit}
              >
                Save
              </LoadingButton>
            </Stack>
          </Stack>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ServicePrincipalConfigModal;
